import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './css/Banner.css';

import BannerCamera from '../../../assets/img/banner/banner-cameras.webp';
import ProductsCamera from '../../../assets/img/banner/prosucts-cameras.png';
import BannerLighting from '../../../assets/img/banner/banner-lighting.jpg';
import ProductsLighting from '../../../assets/img/banner/prosucts-lighting.png';
import BannerSoftware from '../../../assets/img/banner/banner-software.jpg';
import ProductsSoftware from '../../../assets/img/banner/prosucts-software.png';
import BannerSolutions from '../../../assets/img/banner/banner-solutions.jpg';
import ProductsSolutions from '../../../assets/img/banner/prosucts-solutions.png';

const AUTOSLIDE_INTERVAL = 5000;

function Banner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  const bannerData = [
    // ... (保持原本的 data 資料不變)
    {
      id: 'sensor',
      title: t('banner.sensor.title'),
      subtitle: t('banner.sensor.subtitle'),
      description: t('banner.sensor.description'),
      bannerImg: BannerCamera,
      productImg: ProductsCamera,
      link: '/products/camera',
      buttonText: t('banner.sensor.buttonText')
    },
    {
      id: 'lighting',
      title: t('banner.lighting.title'),
      subtitle: t('banner.lighting.subtitle'),
      description: t('banner.lighting.description'),
      bannerImg: BannerLighting,
      productImg: ProductsLighting,
      link: '/products/lighting',
      buttonText: t('banner.lighting.buttonText')
    },
    {
      id: 'software',
      title: t('banner.software.title'),
      subtitle: t('banner.software.subtitle'),
      description: t('banner.software.description'),
      bannerImg: BannerSoftware,
      productImg: ProductsSoftware,
      link: '/products/software',
      buttonText: t('banner.software.buttonText')
    },
    {
      id: 'labeler',
      title: t('banner.automation.title'),
      subtitle: t('banner.automation.subtitle'),
      description: t('banner.automation.description'),
      bannerImg: BannerSolutions,
      productImg: ProductsSolutions,
      link: '/products/solutions',
      buttonText: t('banner.automation.buttonText')
    }
  ];

  const currentBanner = bannerData[currentIndex];
  const isCameraSlide = currentBanner?.id === 'sensor';

  const startAutoslide = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % bannerData.length);
    }, AUTOSLIDE_INTERVAL);
  };

  const stopAutoslide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    // startAutoslide();
    return () => stopAutoslide();
  }, []);

  const handleButtonClick = (data) => {
    const newIndex = bannerData.findIndex(item => item.id === data.id);
    setCurrentIndex(newIndex);
    stopAutoslide();
    startAutoslide();
    navigate(data.link);
  };

  return (
    <div className="banner-wrapper relative w-full overflow-hidden">
      <div className="banner-container relative w-full overflow-hidden min-h-[600px] sm:min-h-[600px] md:min-h-[600px] lg:min-h-[800px] xl:min-h-[900px] 2xl:min-h-[1000px]">
        <img
          src={currentBanner.bannerImg}
          alt="Banner Background"
          className="banner-bg-img absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out"
        />

        {/*
          MODIFIED 1: Absolute Image (Desktop)
          改為 `hidden lg:block`。
          原本是 `md:block`，但在 727px 左右 (md區間) 圖片會跟文字擠在一起。
          改成 lg (1024px) 才顯示右側大圖，避免平板直向時重疊。
        */}
        <img
          src={currentBanner.productImg}
          alt={currentBanner.buttonText}
          className={
            `hidden lg:block absolute h-auto object-contain transition-all duration-500 ease-in-out z-10 ` +
            (!isCameraSlide
              ? 'w-2/6 top-1/4 right-20'
              : 'right-20 top-1/2 -translate-y-1/2 w-1/2')
          }
        />

        {/*
          MODIFIED 2: Banner Content Container
          加入了 `max-w-[90%] sm:max-w-[70%] lg:max-w-[50%]`。
          這確保文字區塊永遠只佔據左側一定比例，不會延伸到右側去跟圖片重疊。
        */}
        <div className="pl-4 banner-content absolute top-1/3 left-8 text-white z-20 md:left-4 sm:left-2 sm:top-1/4 max-w-[90%] sm:max-w-[80%] lg:max-w-[50%]">
          <h1 className="text-5xl font-bold mb-2 md:text-4xl sm:text-3xl whitespace-pre-line">
            {currentBanner.title}
          </h1>
          {currentBanner.subtitle && (
            <h2 className="text-4xl font-semibold mb-4 md:text-3xl sm:text-2xl">
              {currentBanner.subtitle}
            </h2>
          )}

          {/*
            MODIFIED 3: Description Text
            加入了 `max-w-[520px]`。
            這會限制英文描述的寬度，使其大約等同於中文標題的視覺寬度，不會拉得太長。
          */}
          <p className="text-xl mb-6 md:text-lg sm:text-base text-white max-w-[420px]">
            {currentBanner.description}
          </p>

          <button
            type="button"
            onClick={() => navigate(currentBanner.link)}
            className="banner-main-button inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300 md:py-2 md:px-4 sm:py-2 sm:px-3 sm:text-sm"
          >
            {t('banner.learnMore')}
          </button>

          {/*
            MODIFIED 4: Mobile/Tablet Image (Inline)
            原本是 `hidden sm-block`。現在改為 `lg:hidden sm:block`。
            邏輯：在 sm/md (平板/手機) 時顯示這張圖 (放在文字下方)，在 lg (桌機) 時隱藏這張圖 (因為上面那張絕對定位的圖會出現)。
            這樣可以完美解決 727px 時的排版，變成 [文字在上，圖片在下]，不會重疊。
          */}
          <img
            src={currentBanner.productImg}
            alt={currentBanner.buttonText}
            className={
              `lg:hidden sm-block h-auto object-contain transition-all duration-500 ease-in-out z-10` +
              (!isCameraSlide
                ? 'w-2/6 md:w-1/2 sm:w-1/2 px-12'
                : 'w-11/12 md:w-1/2 sm:w-1/2 px-12')
            }
          />
        </div>
      </div>

      <div
        className="banner-nav-buttons-wrapper relative w-full bg-gray-900 bg-opacity-75 z-30"
        onMouseEnter={stopAutoslide}
        onMouseLeave={startAutoslide}
      >
        <div className="banner-nav-buttons flex justify-around p-4 flex-wrap">
          {bannerData.map((data) => (
            <button
              key={data.id}
              type="button"
              onMouseEnter={() => setCurrentIndex(bannerData.findIndex(item => item.id === data.id))}
              onClick={() => handleButtonClick(data)}
              className={`banner-nav-button flex-1 text-white p-3 text-center cursor-pointer transition-colors duration-300 ${currentBanner.id === data.id ? 'bg-blue-700' : 'hover:bg-gray-700'} md:p-2 sm:p-1 sm:text-xs flex flex-col items-center justify-center`}
            >
              <div className="mb-1">
                <img src={data.productImg} alt={data.buttonText} className="h-8 w-8 object-contain sm:h-6 sm:w-6" />
              </div>
              {data.buttonText}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Banner;